import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Calendar, UtensilsCrossed, ShoppingBag, Plus, Trash2, X } from "lucide-react";
import { api, formatPrice } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const TABS = [
  { key: "reservations", label: "Reservations", icon: Calendar },
  { key: "orders", label: "Orders", icon: ShoppingBag },
  { key: "menu", label: "Menu", icon: UtensilsCrossed },
];

const RES_STATUS = ["pending", "confirmed", "cancelled"];
const ORDER_STATUS = ["received", "preparing", "ready", "completed", "cancelled"];
const CATEGORIES = ["small_plates", "indian", "asian", "italian", "bbq", "desserts", "drinks", "cocktails", "wine"];

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("reservations");
  const [reservations, setReservations] = useState([]);
  const [orders, setOrders] = useState([]);
  const [menu, setMenu] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const loadAll = async () => {
    try {
      const [r, o, m] = await Promise.all([
        api.get("/admin/reservations"),
        api.get("/admin/orders"),
        api.get("/admin/menu"),
      ]);
      setReservations(r.data);
      setOrders(o.data);
      setMenu(m.data);
    } catch {
      toast.error("Failed to load data");
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const onLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const updateRes = async (id, status) => {
    try {
      await api.patch(`/admin/reservations/${id}/status`, { status });
      toast.success(`Reservation ${status}`);
      loadAll();
    } catch {
      toast.error("Update failed");
    }
  };

  const updateOrder = async (id, status) => {
    try {
      await api.patch(`/admin/orders/${id}/status`, { status });
      toast.success(`Order ${status}`);
      loadAll();
    } catch {
      toast.error("Update failed");
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`/admin/menu/${id}`);
      toast.success("Deleted");
      loadAll();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen flex bg-[#0F120E] grain">
      <aside className="w-60 border-r border-[#2E3628] p-6 hidden md:flex flex-col">
        <Link to="/" data-testid="admin-home-link">
          <div className="font-display text-2xl tracking-[0.3em] text-[#F9F6F0]">CHICA</div>
          <div className="overline mt-1 text-[10px]">Admin</div>
        </Link>

        <nav className="mt-10 space-y-1 flex-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                data-testid={`admin-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`w-full text-left px-4 py-3 flex items-center gap-3 text-sm transition-colors ${
                  tab === t.key
                    ? "bg-[#1C2219] text-[#D4C1A0] border border-[#2E3628]"
                    : "text-[#9AA894] hover:text-[#F9F6F0]"
                }`}
              >
                <Icon className="w-4 h-4" strokeWidth={1.4} />
                {t.label}
              </button>
            );
          })}
        </nav>

        <div className="text-xs text-[#9AA894] truncate mb-3" data-testid="admin-email">{admin?.email}</div>
        <button
          onClick={onLogout}
          data-testid="admin-logout-btn"
          className="flex items-center gap-2 text-xs text-[#9AA894] hover:text-[#F9F6F0]"
        >
          <LogOut className="w-3 h-3" strokeWidth={1.4} /> Sign out
        </button>
      </aside>

      <main className="flex-1 p-6 md:p-12 overflow-auto">
        {/* Mobile tabs */}
        <div className="md:hidden flex gap-2 mb-6">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-mobile-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-3 py-2 text-[11px] uppercase tracking-widest border ${
                tab === t.key ? "border-[#D4C1A0] text-[#D4C1A0]" : "border-[#2E3628] text-[#9AA894]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="overline">{TABS.find((t) => t.key === tab)?.label}</div>
            <h1 className="font-display text-4xl text-[#F9F6F0] mt-2">
              {tab === "reservations" && `${reservations.length} reservations`}
              {tab === "orders" && `${orders.length} orders`}
              {tab === "menu" && `${menu.length} dishes`}
            </h1>
          </div>
          {tab === "menu" && (
            <button
              data-testid="admin-add-menu-btn"
              onClick={() => { setEditing(null); setShowForm(true); }}
              className="btn-line"
            >
              <Plus className="w-4 h-4" strokeWidth={1.4} /> New dish
            </button>
          )}
        </div>

        {/* Reservations */}
        {tab === "reservations" && (
          <div className="border border-[#2E3628]" data-testid="reservations-table">
            <div className="grid grid-cols-12 px-6 py-4 text-[10px] uppercase tracking-widest text-[#9AA894] border-b border-[#2E3628]">
              <div className="col-span-3">Guest</div>
              <div className="col-span-2">Date / Time</div>
              <div className="col-span-1 text-center">Guests</div>
              <div className="col-span-3">Contact</div>
              <div className="col-span-3">Status</div>
            </div>
            {reservations.length === 0 && (
              <div className="px-6 py-12 text-center text-[#9AA894] text-sm" data-testid="no-reservations">No reservations yet.</div>
            )}
            {reservations.map((r) => (
              <div key={r.id} data-testid={`reservation-${r.id}`} className="grid grid-cols-12 px-6 py-5 border-b border-[#2E3628]/60 items-center text-sm">
                <div className="col-span-3 text-[#F9F6F0]">
                  {r.name}
                  {r.special_requests && <div className="text-[10px] text-[#9AA894] mt-1 line-clamp-1">{r.special_requests}</div>}
                </div>
                <div className="col-span-2 text-[#D4C1A0] font-display text-base">{r.date} · {r.time}</div>
                <div className="col-span-1 text-center text-[#F9F6F0]">{r.guests}</div>
                <div className="col-span-3 text-[#9AA894] text-xs">
                  <div>{r.email}</div>
                  <div>{r.phone}</div>
                </div>
                <div className="col-span-3 flex gap-2 flex-wrap">
                  {RES_STATUS.map((s) => (
                    <button
                      key={s}
                      data-testid={`reservation-${r.id}-set-${s}`}
                      onClick={() => updateRes(r.id, s)}
                      className={`px-2 py-1 text-[10px] uppercase tracking-widest border ${
                        r.status === s ? "border-[#D4C1A0] text-[#D4C1A0]" : "border-[#2E3628] text-[#9AA894] hover:text-[#F9F6F0]"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Orders */}
        {tab === "orders" && (
          <div className="border border-[#2E3628]" data-testid="orders-table">
            <div className="grid grid-cols-12 px-6 py-4 text-[10px] uppercase tracking-widest text-[#9AA894] border-b border-[#2E3628]">
              <div className="col-span-3">Customer</div>
              <div className="col-span-3">Items</div>
              <div className="col-span-2">Total</div>
              <div className="col-span-1">Type</div>
              <div className="col-span-3">Status</div>
            </div>
            {orders.length === 0 && (
              <div className="px-6 py-12 text-center text-[#9AA894] text-sm" data-testid="no-orders">No orders yet.</div>
            )}
            {orders.map((o) => (
              <div key={o.id} data-testid={`order-${o.id}`} className="grid grid-cols-12 px-6 py-5 border-b border-[#2E3628]/60 items-center text-sm">
                <div className="col-span-3 text-[#F9F6F0]">
                  {o.customer_name}
                  <div className="text-[10px] text-[#9AA894] mt-1">{o.customer_phone}</div>
                </div>
                <div className="col-span-3 text-[#9AA894] text-xs">
                  {o.items.map((i) => `${i.quantity}× ${i.name}`).join(" · ")}
                </div>
                <div className="col-span-2 font-display text-lg text-[#D4C1A0]">{formatPrice(o.subtotal)}</div>
                <div className="col-span-1 text-[#F9F6F0] text-xs uppercase">{o.fulfillment}</div>
                <div className="col-span-3 flex gap-1 flex-wrap">
                  {ORDER_STATUS.map((s) => (
                    <button
                      key={s}
                      data-testid={`order-${o.id}-set-${s}`}
                      onClick={() => updateOrder(o.id, s)}
                      className={`px-2 py-1 text-[10px] uppercase tracking-widest border ${
                        o.status === s ? "border-[#D4C1A0] text-[#D4C1A0]" : "border-[#2E3628] text-[#9AA894] hover:text-[#F9F6F0]"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Menu */}
        {tab === "menu" && (
          <div className="border border-[#2E3628]" data-testid="menu-table">
            <div className="grid grid-cols-12 px-6 py-4 text-[10px] uppercase tracking-widest text-[#9AA894] border-b border-[#2E3628]">
              <div className="col-span-4">Dish</div>
              <div className="col-span-2">Category</div>
              <div className="col-span-2">Price</div>
              <div className="col-span-2">Available</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
            {menu.map((m) => (
              <div key={m.id} data-testid={`menu-row-${m.id}`} className="grid grid-cols-12 px-6 py-5 border-b border-[#2E3628]/60 items-center text-sm">
                <div className="col-span-4">
                  <div className="font-display text-lg text-[#F9F6F0]">{m.name}</div>
                  <div className="text-[10px] text-[#9AA894] mt-1 line-clamp-1">{m.description}</div>
                </div>
                <div className="col-span-2 text-[#9AA894] text-xs uppercase tracking-widest">{m.category}</div>
                <div className="col-span-2 text-[#D4C1A0] font-display text-lg">{formatPrice(m.price)}</div>
                <div className="col-span-2">
                  <span className={`text-[10px] uppercase tracking-widest ${m.available ? "text-[#D4C1A0]" : "text-[#9AA894]"}`}>
                    {m.available ? "Yes" : "No"}
                  </span>
                </div>
                <div className="col-span-2 flex justify-end gap-3">
                  <button
                    data-testid={`menu-edit-${m.id}`}
                    onClick={() => { setEditing(m); setShowForm(true); }}
                    className="text-[10px] uppercase tracking-widest text-[#9AA894] hover:text-[#D4C1A0]"
                  >
                    Edit
                  </button>
                  <button
                    data-testid={`menu-delete-${m.id}`}
                    onClick={() => deleteItem(m.id)}
                    className="text-[#9AA894] hover:text-[#E6D5D6]"
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={1.4} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {showForm && (
          <MenuForm
            initial={editing}
            onClose={() => { setShowForm(false); setEditing(null); }}
            onSaved={() => { setShowForm(false); setEditing(null); loadAll(); }}
          />
        )}
      </main>
    </div>
  );
}

function MenuForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: initial?.name || "",
    description: initial?.description || "",
    price: initial?.price || "",
    category: initial?.category || "appetizers",
    image: initial?.image || "",
    available: initial?.available ?? true,
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, price: Number(form.price) };
      if (initial) {
        await api.put(`/admin/menu/${initial.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/admin/menu", payload);
        toast.success("Created");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-6" data-testid="menu-form-modal">
      <div className="bg-[#0F120E] border border-[#2E3628] p-10 w-full max-w-xl">
        <div className="flex justify-between items-center mb-8">
          <div>
            <div className="overline">{initial ? "Edit" : "New"}</div>
            <h2 className="font-display text-3xl text-[#F9F6F0] mt-1">
              {initial ? "Edit dish" : "Add new dish"}
            </h2>
          </div>
          <button onClick={onClose} data-testid="menu-form-close" className="text-[#9AA894] hover:text-[#F9F6F0]">
            <X className="w-5 h-5" strokeWidth={1.2} />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-6" data-testid="menu-form">
          <div>
            <label className="overline">Name</label>
            <input data-testid="menu-form-name" required className="input-line mt-2" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <label className="overline">Description</label>
            <textarea data-testid="menu-form-description" required rows={3} className="input-line mt-2" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="overline">Price (₹)</label>
              <input type="number" min="0" step="1" data-testid="menu-form-price" required className="input-line mt-2" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
            </div>
            <div>
              <label className="overline">Category</label>
              <select data-testid="menu-form-category" className="input-line mt-2 appearance-none bg-[#0F120E]" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="overline">Image URL (optional)</label>
            <input data-testid="menu-form-image" className="input-line mt-2" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} />
          </div>
          <label className="flex items-center gap-3 text-sm text-[#9AA894]">
            <input data-testid="menu-form-available" type="checkbox" checked={form.available} onChange={(e) => setForm({ ...form, available: e.target.checked })} />
            Available on menu
          </label>
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={loading} className="btn-solid" data-testid="menu-form-save">
              {loading ? "Saving…" : initial ? "Save changes" : "Create"}
            </button>
            <button type="button" onClick={onClose} className="btn-line" data-testid="menu-form-cancel">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
